/**
 * h5/src/utils/tauri-bridge.ts
 * Tauri invoke 命令 TypeScript 封装层
 *
 * R2 落地：
 *   - 仅在 Tauri 环境（window.__TAURI__ 存在）下调用 invoke
 *   - 非 Tauri 环境降级返回 null / 抛出降级提示（调用方处理）
 *   - secret 永不在 TS 层出现（ws_sign 返回的 WsAuthQuery 不含 secret）
 *
 * 使用方式：
 *   import { wsSign, isTauri, getKioskInfo } from '@/utils/tauri-bridge'
 *   if (isTauri()) {
 *     const auth = await wsSign(machineCode)
 *   }
 */

// ─── 类型定义 ────────────────────────────────────────────────────────────────

/** WS 连接鉴权参数（Rust ws_sign 命令返回值，不含 secret） */
export interface WsAuthQuery {
  code: string
  ts: number
  nonce: string
  sig: string
}

/** REST Ed25519 签名（Rust generate_request_signature 返回值） */
export interface RequestSignature {
  timestamp: number
  nonce: string
  signature: string
}

/** Kiosk 机器信息 */
export interface KioskInfo {
  platform: 'macos' | 'windows' | 'linux' | 'android' | 'ios' | string
  arch: 'x86_64' | 'aarch64' | string
  app_version: string
  machine_code: string
}

// ─── 环境检测 ────────────────────────────────────────────────────────────────

/**
 * 检测当前是否运行在 Tauri 环境中
 *
 * 判断依据：window.__TAURI__ 是否存在
 * 注意：此函数可在 SSR / Worker 中安全调用（typeof window 保护）
 */
export function isTauri(): boolean {
  return typeof window !== 'undefined' && (
    '__TAURI__' in window ||           // Tauri v1
    '__TAURI_INTERNALS__' in window   // Tauri v2
  )
}

/**
 * 获取 Tauri invoke 函数
 * 懒加载 @tauri-apps/api/core，避免在非 Tauri 环境中导入失败
 */
async function getInvoke(): Promise<((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null> {
  if (!isTauri()) return null
  try {
    const { invoke } = await import('@tauri-apps/api/core')
    return invoke as (cmd: string, args?: Record<string, unknown>) => Promise<unknown>
  } catch {
    return null
  }
}

// ─── 核心命令封装 ────────────────────────────────────────────────────────────

/**
 * R2 核心：生成 WS 连接鉴权参数
 *
 * 调用 Rust ws_sign 命令，secret 在 Rust 侧处理，TS 永远拿不到 secret 明文。
 * 非 Tauri 环境返回 null（调用方应降级到 TicketSecretProvider）。
 *
 * @param machineCode 机器码
 * @returns WsAuthQuery（含 code/ts/nonce/sig）或 null（非 Tauri 环境）
 */
export async function wsSign(machineCode: string): Promise<WsAuthQuery | null> {
  const invoke = await getInvoke()
  if (!invoke) return null
  return invoke('ws_sign', { machineCode }) as Promise<WsAuthQuery>
}

/**
 * 存储机器 secret（供首次配置使用，UI 在 Phase F 实现）
 *
 * @param code 机器码
 * @param secret HMAC-SHA256 密钥（来自后台管理配置）
 */
export async function generateRequestSignature(
  machineCode: string,
  method: string,
  path: string,
  body = '',
): Promise<RequestSignature | null> {
  const invoke = await getInvoke()
  if (!invoke) return null
  return invoke('generate_request_signature', {
    machineCode,
    method,
    path,
    body,
  }) as Promise<RequestSignature>
}

export async function getEnrollKeyt(): Promise<string | null> {
  const invoke = await getInvoke()
  if (!invoke) return null
  return invoke('get_enroll_keyt') as Promise<string>
}

export async function regenerateEnrollKeyt(): Promise<string | null> {
  const invoke = await getInvoke()
  if (!invoke) return null
  return invoke('regenerate_enroll_keyt') as Promise<string>
}

export async function hasEnrollKeypair(): Promise<boolean> {
  const invoke = await getInvoke()
  if (!invoke) return false
  return invoke('has_enroll_keypair') as Promise<boolean>
}

export async function setMachineCode(code: string): Promise<string> {
  const invoke = await getInvoke()
  if (!invoke) throw new Error('[tauri-bridge] setMachineCode: 非 Tauri 环境')
  return invoke('set_machine_code', { code }) as Promise<string>
}

export async function setSecret(code: string, secret: string): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) throw new Error('[tauri-bridge] setSecret: 非 Tauri 环境，无法存储 secret')
  await invoke('set_secret', { code, secret })
}

/**
 * 检查本地是否已写入 secret（不返回明文）
 * 兼容旧版 __admin__ key
 */
export async function hasSecret(code = '__master__'): Promise<boolean> {
  const invoke = await getInvoke()
  if (!invoke) return false
  if (await invoke('has_secret', { code }) as boolean) return true
  if (code === '__master__') {
    return invoke('has_secret', { code: '__admin__' }) as Promise<boolean>
  }
  return false
}

/**
 * 删除机器 secret
 */
export async function deleteSecret(code: string): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) throw new Error('[tauri-bridge] deleteSecret: 非 Tauri 环境')
  await invoke('delete_secret', { code })
}

// ─── Kiosk 信息 ──────────────────────────────────────────────────────────────

/**
 * 获取 Kiosk 机器信息（平台/架构/版本/机器码）
 *
 * 非 Tauri 环境返回浏览器侧兜底信息
 */
export async function getKioskInfo(): Promise<KioskInfo> {
  const invoke = await getInvoke()
  if (!invoke) {
    // 浏览器降级
    return {
      platform: 'browser',
      arch: 'unknown',
      app_version: '0.0.0-browser',
      machine_code: getBrowserMachineCode(),
    }
  }
  return invoke('get_kiosk_info') as Promise<KioskInfo>
}

/**
 * 获取机器码（简化版）
 */
export async function getMachineId(): Promise<string> {
  const invoke = await getInvoke()
  if (!invoke) return getBrowserMachineCode()
  return invoke('get_machine_id') as Promise<string>
}

/** 浏览器环境下的机器码兜底（用 localStorage 持久化） */
function getBrowserMachineCode(): string {
  const key = 'kiosk_machine_code'
  let code = localStorage.getItem(key)
  if (!code) {
    code = `BROWSER-${Math.random().toString(36).slice(2, 18).toUpperCase()}`
    localStorage.setItem(key, code)
  }
  return code
}

// ─── F.4 R4 lock_ack 埋点 ────────────────────────────────────────────────────

/**
 * F.4 lock_ack 埋点：上报 Tauri 拦截到的锁定事件
 *
 * 触发时机：
 *   - Tauri shell 拦截窗口关闭（window_close）
 *   - 拦截系统快捷键（hotkey，如 Alt+F4 / Cmd+Q）
 *   - 拦截菜单关闭（menu）
 *
 * H5 模式（非 Tauri）无 lock 机制，此函数直接返回
 *
 * @param lockType 锁定类型
 * @param hotkey   快捷键字符串（仅 lock_type=hotkey 时传入）
 */
export async function reportLockAck(
  lockType: 'window_close' | 'hotkey' | 'menu',
  hotkey?: string,
): Promise<void> {
  if (!isTauri()) return // H5 模式跳过

  try {
    const { getTelemetry } = await import('@/composables/useTelemetry')
    const payload: Record<string, unknown> = { lock_type: lockType }
    if (hotkey) payload['hotkey'] = hotkey
    getTelemetry().track('lock_ack', payload)
  } catch {
    // 静默，不影响锁定逻辑
  }
}

// ─── R4 Kiosk 模式控制 ───────────────────────────────────────────────────────

/**
 * 启用 Kiosk 锁屏模式（R4 第二层防护）
 *
 * 生产模式：全屏 + always_on_top + 禁关闭 + 禁右键
 * 调试模式：仅注入右键禁用，不强制全屏
 */
export async function enableKioskMode(): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) return // 浏览器环境跳过
  await invoke('enable_kiosk_mode')
}

/**
 * 禁用 Kiosk 模式（需要管理员令牌）
 *
 * @param adminToken 环境变量 KIOSK_ADMIN_TOKEN 中配置的令牌
 */
export async function disableKioskMode(adminToken: string): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) return
  await invoke('disable_kiosk_mode', { adminToken })
}

/**
 * 设置窗口置顶状态
 */
export async function setAlwaysOnTop(on: boolean): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) return
  await invoke('set_always_on_top', { on })
}

/**
 * 查询当前是否处于 Kiosk 全屏模式
 */
export async function isKioskModeActive(): Promise<boolean> {
  const invoke = await getInvoke()
  if (!invoke) return false
  return invoke('is_kiosk_mode_active') as Promise<boolean>
}

/**
 * 获取持久化存储的窗口模式 (kiosk | windowed)
 */
export async function getWindowMode(): Promise<'kiosk' | 'windowed'> {
  const invoke = await getInvoke()
  if (!invoke) return 'kiosk'
  return invoke('get_window_mode') as Promise<'kiosk' | 'windowed'>
}

/**
 * 设置并持久化窗口模式
 */
export async function setWindowMode(mode: 'kiosk' | 'windowed'): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) return
  await invoke('set_window_mode', { mode })
}

// ─── 自启动管理 ──────────────────────────────────────────────────────────────

/**
 * 启用系统自启动
 */
export async function enableAutostart(): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) throw new Error('[tauri-bridge] enableAutostart: 非 Tauri 环境')
  await invoke('enable_autostart')
}

/**
 * 禁用系统自启动
 */
export async function disableAutostart(): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) throw new Error('[tauri-bridge] disableAutostart: 非 Tauri 环境')
  await invoke('disable_autostart')
}

/**
 * 查询自启动是否已启用
 */
export async function isAutostartEnabled(): Promise<boolean> {
  const invoke = await getInvoke()
  if (!invoke) return false
  return invoke('is_autostart_enabled') as Promise<boolean>
}

// ─── 远程指令：退出 / 重启 ──────────────────────────────────────────────────

/**
 * 退出 Kiosk 应用（shell_quit 远程指令）
 * Tauri 环境：invoke quit_app → app.exit(0)
 * 浏览器降级：无操作（不支持）
 */
export async function quitApp(): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) {
    return // 非 Tauri 环境，跳过
    return
  }
  await invoke('quit_app')
}

/**
 * 重启 Kiosk 应用（reboot 远程指令）
 * Tauri 环境：invoke restart_app → app.restart()
 * 浏览器降级：window.location.reload()（模拟刷新）
 */
export async function restartApp(): Promise<void> {
  const invoke = await getInvoke()
  if (!invoke) {
    window.location.reload()
    return
  }
  await invoke('restart_app')
}

/**
 * 刷新页面（refresh 远程指令）
 * Tauri / 浏览器均支持
 */
export function refreshApp(): void {
  window.location.reload()
}

// ─── 运维 ops / 指纹 / 串口 ──────────────────────────────────────────────────

export interface CardshopOpsRuntimePatch {
  api_base_url?: string | null
  machine_code?: string | null
  bill_channel?: string | null
  serial_device?: string | null
  serial_com?: string | null
  serial_baud?: number | null
  update_check_mode?: string | null
  update_check_daily_at?: string | null
  update_check_weekly_dow?: number | null
  autostart_enabled?: boolean | null
}

export async function getOpsConfig(): Promise<CardshopOpsRuntimePatch | null> {
  const invoke = await getInvoke()
  if (!invoke) return null
  return invoke('get_ops_config') as Promise<CardshopOpsRuntimePatch>
}

export async function saveOpsConfig(patch: CardshopOpsRuntimePatch): Promise<CardshopOpsRuntimePatch | null> {
  const invoke = await getInvoke()
  if (!invoke) return null
  return invoke('save_ops_config', { patch }) as Promise<CardshopOpsRuntimePatch>
}

export async function getDeviceFingerprintHash(): Promise<string> {
  const invoke = await getInvoke()
  if (!invoke) return ''
  try {
    return (await invoke('get_device_fingerprint_hash')) as string
  } catch {
    return ''
  }
}

export async function listSerialPorts(): Promise<string[]> {
  const invoke = await getInvoke()
  if (!invoke) return []
  const list = (await invoke('list_serial_ports')) as Array<{ name: string }>
  return (list || []).map((p) => p.name).filter(Boolean)
}
